import { validateURL, validateUSPhoneNumber } from "../utils"
import {
  applyAdditionalOfficePayloadRules,
  buildAdditionalOfficeDisplayBlocks,
  buildAdditionalOfficeDisplayRows,
} from "./additionalOfficeIntakeFields"
import {
  INTAKE_BRAND_OPTIONS,
  STATE_OPTIONS,
  TIMEZONE_OPTIONS,
  brandOptionLabel,
  normalizeBrandValue,
  normalizeStateValue,
  normalizeTimezoneValue,
  stateOptionLabel,
  timezoneOptionLabel,
} from "./brokerageProfileIntakeChoices"
import { RECRUIT_PROFILE_SECTION_TITLES } from "./recruitProfileSectionStyles"

export {
  INTAKE_BRAND_OPTIONS,
  STATE_OPTIONS,
  TIMEZONE_OPTIONS,
  brandOptionLabel,
  normalizeBrandValue,
  normalizeStateValue,
  normalizeTimezoneValue,
  stateOptionLabel,
  timezoneOptionLabel,
} from "./brokerageProfileIntakeChoices"

export const BROKERAGE_PROFILE_INTAKE_API_PATH =
  "/crm/api/brokerage-profile-intake/"

const INTAKE_SNAPSHOT_STORAGE_KEY = "brokerage_profile_intake_snapshot"

/** Session fallback when GET is not deployed yet (e.g. api-dev before release). */
export function readIntakeSnapshot() {
  try {
    const raw = sessionStorage.getItem(INTAKE_SNAPSHOT_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed?.intake || typeof parsed.intake !== "object") {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function writeIntakeSnapshot({ submitted, submitted_at, intake }) {
  try {
    sessionStorage.setItem(
      INTAKE_SNAPSHOT_STORAGE_KEY,
      JSON.stringify({ submitted, submitted_at, intake })
    )
  } catch {
    // ignore quota / private mode
  }
}

export const YES_NO_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
]

export const GENDER_OPTIONS = [
  { value: "He/Him", label: "He/Him" },
  { value: "She/Her", label: "She/Her" },
  { value: "They/Them", label: "They/Them" },
]

export const BROKER_OF_RECORD_DETAIL_FIELD_KEYS = [
  "brokerOfRecord",
  "brokerOfRecordPhonetics",
  "brokerOfRecordPhone",
  "brokerOfRecordEmail",
  "brokerOfRecordGender",
]

export function parseIntakeBoolean(value) {
  if (typeof value === "boolean") {
    return value
  }
  if (typeof value === "string") {
    return ["true", "1", "yes", "on"].includes(value.trim().toLowerCase())
  }
  return Boolean(value)
}

export function isDifferentBrokerOfRecord(intake) {
  const value =
    intake?.differentBrokerOfRecord ?? intake?.different_broker_of_record
  return parseIntakeBoolean(value)
}

/** Profile tab card title (Settings → Profile). */
export const RECRUITING_CAMPAIGN_PROFILE_TITLE =
  "Set up your Recruiting Campaign profile"

export const BROKERAGE_TYPE_OPTIONS = [
  { value: "Brick and Mortar", label: "Brick and Mortar" },
  { value: "Virtual", label: "Virtual" },
  { value: "Hybrid", label: "Hybrid" },
]

export const CREDIBILITY_STATEMENT_EXAMPLES = [
  "We have won the most awards",
  "Fastest growing brokerage",
  "#1 brokerages",
]

export const CREDIBILITY_STATEMENT_HELPER_TEXT = `Example:\n${CREDIBILITY_STATEMENT_EXAMPLES.map(
  (example) => `• ${example}`
).join("\n")}`

export const PROFESSIONAL_BIO_HIGHLIGHTS_HELPER_TEXT =
  "Career and business highlights beyond your brokerage profile. Example: Former team lead, 15 years in luxury residential, published market analyst."

export const PERSONAL_BIO_HIGHLIGHTS_HELPER_TEXT =
  "Personal details that help personalize outreach. Example: Ex-hockey player, four kids, passion for photography."

/** Prepends https:// when the user omits a scheme. */
export function normalizeBrokerageWebsiteUrl(raw) {
  const trimmed = String(raw ?? "").trim()
  if (!trimmed) {
    return ""
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`
  }
  return trimmed
}

/** User-facing label for the brokerage About / Recruiting page URL field. */
export const WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL =
  "Website About or Recruiting Page"

/** Required + valid http(s) URL for the brokerage website field. */
export function validateBrokerageWebsiteUrl(raw) {
  const trimmed = String(raw ?? "").trim()
  if (!trimmed) {
    return {
      valid: false,
      normalized: "",
      message: `${WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL} is required`,
    }
  }
  const normalized = normalizeBrokerageWebsiteUrl(trimmed)
  if (!validateURL(normalized)) {
    return {
      valid: false,
      normalized,
      message: `Enter a valid ${WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL} (e.g. https://www.yourbrokerage.com)`,
    }
  }
  return { valid: true, normalized, message: "" }
}

/** Objection + recommendation/handler pairs (1:1, stored as existing intake fields). */
export const OBJECTION_RECOMMENDATION_PAIRS = [1, 2, 3].map((n) => ({
  index: n,
  objectionKey: `objection${n}`,
  objectionLabel: `Objection ${n}`,
  recommendationKey: `recommendation${n}`,
  recommendationLabel: `Recommendation / Handler ${n}`,
}))

export const ACCEPT_NEW_AGENTS_OPTIONS = [
  { value: "no", label: "No" },
  { value: "licensed_only", label: "Yes - Licensed Only" },
  {
    value: "licensed_and_pre_licensed",
    label: "Yes - licensed and pre-licensed",
  },
]

/** camelCase form key + display label for required validation */
export const REQUIRED_INTAKE_FIELD_DEFS = [
  {
    key: "websiteUrl",
    label: WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL,
    fromWebsiteUrl: true,
  },
  { key: "firstName", label: "First Name" },
  { key: "firstNamePhonetics", label: "First Name Phonetics" },
  { key: "lastName", label: "Last Name" },
  { key: "lastNamePhonetics", label: "Last Name Phonetics" },
  { key: "title", label: "Title" },
  { key: "email", label: "Email" },
  { key: "mobileNumber", label: "Mobile Phone Number" },
  { key: "gender", label: "Gender" },
  { key: "brokerageName", label: "Brokerage Name" },
  { key: "brokerageNamePhonetics", label: "Brokerage Name Phonetics" },
  { key: "brokerageType", label: "Brokerage Type" },
  { key: "brokeragePhone", label: "Brokerage Phone Number" },
  { key: "brand", label: "Brand" },
  { key: "streetAddress", label: "Primary Brokerage Address (Street Only)" },
  { key: "streetAddressPhonetics", label: "Street Name Phonetics" },
  { key: "city", label: "Primary City" },
  { key: "cityPhonetics", label: "City Name Phonetics" },
  { key: "state", label: "State" },
  { key: "timezone", label: "Time Zone" },
  { key: "zipcode", label: "Zip Code" },
  {
    key: "localLandmark",
    label: "Nearest Intersection or Local Landmark",
  },
  {
    key: "localLandmarkPhonetics",
    label: "Intersection or Landmark Phonetics",
  },
  { key: "numberOfOffices", label: "No. of Offices" },
  { key: "numberOfAgents", label: "No. of Agents" },
  { key: "yearsInBusiness", label: "Years in Business" },
  { key: "credibilityStatement", label: "Credibility Statement" },
  { key: "keyFeature1", label: "Key Feature 1" },
  { key: "keyFeature1Short", label: "Key Feature 1 Short" },
  { key: "keyFeature2", label: "Key Feature 2" },
  { key: "keyFeature2Short", label: "Key Feature 2 Short" },
  { key: "keyFeature3", label: "Key Feature 3" },
  { key: "keyFeature3Short", label: "Key Feature 3 Short" },
  { key: "acceptPartTimers", label: "Accept Part Timers?" },
  { key: "acceptCommercialAgents", label: "Commercial Agents?" },
  { key: "acceptPropertyManagers", label: "Property Managers?" },
  { key: "acceptLoanOfficers", label: "Mortgage Brokers?" },
]

const SNAKE_TO_CAMEL = {
  first_name: "firstName",
  first_name_phonetics: "firstNamePhonetics",
  last_name: "lastName",
  last_name_phonetics: "lastNamePhonetics",
  title: "title",
  email: "email",
  mobile_number: "mobileNumber",
  gender: "gender",
  brokerage_name: "brokerageName",
  brokerage_name_phonetics: "brokerageNamePhonetics",
  brokerage_type: "brokerageType",
  broker_of_record: "brokerOfRecord",
  broker_of_record_phonetics: "brokerOfRecordPhonetics",
  broker_of_record_phone: "brokerOfRecordPhone",
  broker_of_record_email: "brokerOfRecordEmail",
  broker_of_record_gender: "brokerOfRecordGender",
  brokerage_phone: "brokeragePhone",
  brand: "brand",
  street_address: "streetAddress",
  street_address_phonetics: "streetAddressPhonetics",
  city: "city",
  city_phonetics: "cityPhonetics",
  state: "state",
  zipcode: "zipcode",
  timezone: "timezone",
  local_landmark: "localLandmark",
  local_landmark_phonetics: "localLandmarkPhonetics",
  number_of_agents: "numberOfAgents",
  years_in_business: "yearsInBusiness",
  number_of_offices: "numberOfOffices",
  additional_office: "additionalOffice",
  additional_office_address_1: "additionalOfficeAddress1",
  office_1_intersection_landmark: "office1IntersectionLandmark",
  office_1_contact_name: "office1ContactName",
  office_1_phonetics: "office1Phonetics",
  second_additional_office: "secondAdditionalOffice",
  additional_office_address_2: "additionalOfficeAddress2",
  office_2_intersection_landmark: "office2IntersectionLandmark",
  office_2_contact_name: "office2ContactName",
  office_2_phonetics: "office2Phonetics",
  third_additional_office: "thirdAdditionalOffice",
  additional_office_address_3: "additionalOfficeAddress3",
  office_3_intersection_landmark: "office3IntersectionLandmark",
  office_3_contact_name: "office3ContactName",
  office_3_phonetics: "office3Phonetics",
  four_plus_offices: "fourPlusOffices",
  additional_offices: "additionalOffices",
  credibility_statement: "credibilityStatement",
  professional_bio_highlights: "professionalBioHighlights",
  personal_bio_highlights: "personalBioHighlights",
  objection_1: "objection1",
  objection_2: "objection2",
  objection_3: "objection3",
  recommendation_1: "recommendation1",
  recommendation_2: "recommendation2",
  recommendation_3: "recommendation3",
  key_feature_1: "keyFeature1",
  key_feature_1_short: "keyFeature1Short",
  key_feature_2: "keyFeature2",
  key_feature_2_short: "keyFeature2Short",
  key_feature_3: "keyFeature3",
  key_feature_3_short: "keyFeature3Short",
  accept_part_timers: "acceptPartTimers",
  accept_commercial_agents: "acceptCommercialAgents",
  accept_property_managers: "acceptPropertyManagers",
  accept_loan_officers: "acceptLoanOfficers",
  accept_new_agents: "acceptNewAgents",
}

/** CamelCase booleans omitted from SNAKE_TO_CAMEL (not string fields). */
const INTAKE_BOOLEAN_CAMEL_TO_SNAKE = {
  differentBrokerOfRecord: "different_broker_of_record",
  additionalOffice: "additional_office",
  secondAdditionalOffice: "second_additional_office",
  thirdAdditionalOffice: "third_additional_office",
  fourPlusOffices: "four_plus_offices",
}

function brokerOfRecordFieldsPopulated(fields) {
  return BROKER_OF_RECORD_DETAIL_FIELD_KEYS.some(
    (key) => String(fields[key] ?? "").trim() !== ""
  )
}

export function emptyForm() {
  return {
    ...Object.fromEntries(
      [
        ...new Set([
          ...Object.values(SNAKE_TO_CAMEL),
          ...REQUIRED_INTAKE_FIELD_DEFS.filter((d) => !d.fromWebsiteUrl).map(
            (d) => d.key
          ),
        ]),
      ].map((k) => [k, ""])
    ),
    differentBrokerOfRecord: false,
    additionalOffice: false,
    secondAdditionalOffice: false,
    thirdAdditionalOffice: false,
    fourPlusOffices: false,
  }
}

export function digitsOnlyPhone(raw) {
  return String(raw ?? "").replace(/\D/g, "")
}

/** Letters, spaces, hyphens, apostrophes, periods (no digits). */
const NAME_PATTERN = /^[a-zA-Z\s'.-]+$/

export function validatePersonName(value, { required = true } = {}) {
  const raw = String(value ?? "")
  const trimmed = raw.trim()
  if (!trimmed) {
    return required
      ? { valid: false, message: "Required" }
      : { valid: true, message: "" }
  }
  if (/\d/.test(trimmed)) {
    return { valid: false, message: "Only letters are allowed (no numbers)." }
  }
  if (!NAME_PATTERN.test(trimmed)) {
    return {
      valid: false,
      message: "Only letters are allowed (no numbers or special characters).",
    }
  }
  return { valid: true, message: "" }
}

/** Matches Settings → Profile phone validation. */
export function validateMobileNumber(value) {
  return validateUSPhoneNumber(value, { required: true })
}

export function validateBrokeragePhoneNumber(value) {
  return validateUSPhoneNumber(value, { required: true })
}

export function normalizeGenderValue(value) {
  const s = String(value ?? "").trim()
  const match = GENDER_OPTIONS.find(
    (o) =>
      o.value.toLowerCase() === s.toLowerCase() ||
      o.label.toLowerCase() === s.toLowerCase()
  )
  return match ? match.value : ""
}

export function genderOptionLabel(value) {
  return normalizeGenderValue(value) || "—"
}

export function normalizeBrokerageTypeValue(value) {
  const s = String(value ?? "").trim()
  if (!s) return ""
  const match = BROKERAGE_TYPE_OPTIONS.find(
    (o) =>
      o.value.toLowerCase() === s.toLowerCase() ||
      o.label.toLowerCase() === s.toLowerCase()
  )
  return match ? match.value : s
}

export function brokerageTypeOptionLabel(value) {
  return normalizeBrokerageTypeValue(value) || "—"
}

function mapGenderFromApi(value) {
  return normalizeGenderValue(value)
}

function mapYesNoFromApi(value) {
  const s = String(value ?? "")
    .trim()
    .toLowerCase()
  if (s === "yes" || s === "y" || s === "true") return "yes"
  if (s === "no" || s === "n" || s === "false" || s === "not") return "no"
  return ""
}

function mapAcceptNewAgentsFromApi(value) {
  const s = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
  if (s === "licensed_only" || s.includes("licensed_only"))
    return "licensed_only"
  if (s.includes("pre") && s.includes("licensed"))
    return "licensed_and_pre_licensed"
  if (s === "no") return "no"
  return ""
}

function fieldHasValue(form, websiteUrl, key) {
  if (key === "websiteUrl") {
    return Boolean((websiteUrl || "").trim())
  }
  const v = form[key]
  if (v === null || v === undefined) return false
  return String(v).trim() !== ""
}

/** Recommendation is required whenever the matching objection has a value. */
export function validateObjectionRecommendationPairs(form) {
  const fieldErrors = {}
  const missingLabels = []
  for (const pair of OBJECTION_RECOMMENDATION_PAIRS) {
    const hasObjection = String(form?.[pair.objectionKey] ?? "").trim() !== ""
    const hasRecommendation =
      String(form?.[pair.recommendationKey] ?? "").trim() !== ""
    if (hasObjection && !hasRecommendation) {
      fieldErrors[pair.recommendationKey] = "Required"
      missingLabels.push(pair.recommendationLabel)
    }
  }
  const valid = missingLabels.length === 0
  return {
    valid,
    fieldErrors,
    missingLabels,
    message: valid
      ? ""
      : `Looks like you missed some of the required fields:\n\n${missingLabels.join(
          "\n"
        )}`,
  }
}

/** Client-side required check (mirrors backend). */
export function validateIntakeForm(form, websiteUrl) {
  const missingLabels = []
  const fieldErrors = {}

  for (const { key, label, fromWebsiteUrl } of REQUIRED_INTAKE_FIELD_DEFS) {
    if (fromWebsiteUrl) {
      continue
    }
    if (!fieldHasValue(form, websiteUrl, key)) {
      missingLabels.push(label)
      fieldErrors[key] = "Required"
    }
  }

  const websiteCheck = validateBrokerageWebsiteUrl(websiteUrl || "")
  if (!websiteCheck.valid) {
    if (!missingLabels.includes(WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL)) {
      missingLabels.push(WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL)
    }
    fieldErrors.websiteUrl = websiteCheck.message
  }

  const firstNameCheck = validatePersonName(form.firstName)
  if (!firstNameCheck.valid) {
    if (!fieldHasValue(form, websiteUrl, "firstName")) {
      if (!missingLabels.includes("First Name")) {
        missingLabels.push("First Name")
      }
    }
    fieldErrors.firstName = firstNameCheck.message
  }

  const lastNameCheck = validatePersonName(form.lastName)
  if (!lastNameCheck.valid) {
    if (!fieldHasValue(form, websiteUrl, "lastName")) {
      if (!missingLabels.includes("Last Name")) {
        missingLabels.push("Last Name")
      }
    }
    fieldErrors.lastName = lastNameCheck.message
  }

  const mobileCheck = validateMobileNumber(form.mobileNumber)
  if (!mobileCheck.valid) {
    if (!missingLabels.includes("Mobile Phone Number")) {
      missingLabels.push("Mobile Phone Number")
    }
    fieldErrors.mobileNumber = mobileCheck.message
  }

  const brokeragePhoneCheck = validateBrokeragePhoneNumber(form.brokeragePhone)
  if (!brokeragePhoneCheck.valid) {
    if (!missingLabels.includes("Brokerage Phone Number")) {
      missingLabels.push("Brokerage Phone Number")
    }
    fieldErrors.brokeragePhone = brokeragePhoneCheck.message
  }

  if (isDifferentBrokerOfRecord(form)) {
    if (fieldHasValue(form, websiteUrl, "brokerOfRecordPhone")) {
      const borPhoneCheck = validateUSPhoneNumber(form.brokerOfRecordPhone, {
        required: false,
      })
      if (!borPhoneCheck.valid) {
        if (!missingLabels.includes("Broker of Record Phone Number")) {
          missingLabels.push("Broker of Record Phone Number")
        }
        fieldErrors.brokerOfRecordPhone = borPhoneCheck.message
      }
    }

    if (
      fieldHasValue(form, websiteUrl, "brokerOfRecordGender") &&
      !normalizeGenderValue(form.brokerOfRecordGender)
    ) {
      if (!missingLabels.includes("Broker of Record Gender")) {
        missingLabels.push("Broker of Record Gender")
      }
      fieldErrors.brokerOfRecordGender = "Select He/Him, She/Her, or They/Them"
    }
  }

  if (
    fieldHasValue(form, websiteUrl, "gender") &&
    !normalizeGenderValue(form.gender)
  ) {
    if (!missingLabels.includes("Gender")) {
      missingLabels.push("Gender")
    }
    fieldErrors.gender = "Select He/Him, She/Her, or They/Them"
  }

  if (
    fieldHasValue(form, websiteUrl, "brokerageType") &&
    !BROKERAGE_TYPE_OPTIONS.some(
      (o) => o.value === normalizeBrokerageTypeValue(form.brokerageType)
    )
  ) {
    if (!missingLabels.includes("Brokerage Type")) {
      missingLabels.push("Brokerage Type")
    }
    fieldErrors.brokerageType = "Select Brick and Mortar, Virtual, or Hybrid"
  }

  if (
    fieldHasValue(form, websiteUrl, "timezone") &&
    !TIMEZONE_OPTIONS.some(
      (o) => o.value === normalizeTimezoneValue(form.timezone)
    )
  ) {
    if (!missingLabels.includes("Time Zone")) {
      missingLabels.push("Time Zone")
    }
    fieldErrors.timezone = "Select a time zone from the list"
  }

  if (
    fieldHasValue(form, websiteUrl, "brand") &&
    !normalizeBrandValue(form.brand)
  ) {
    if (!missingLabels.includes("Brand")) {
      missingLabels.push("Brand")
    }
    fieldErrors.brand = "Select a brand from the list"
  }

  if (
    fieldHasValue(form, websiteUrl, "state") &&
    !normalizeStateValue(form.state)
  ) {
    if (!missingLabels.includes("State")) {
      missingLabels.push("State")
    }
    fieldErrors.state = "Select a state or province from the list"
  }

  const pairResult = validateObjectionRecommendationPairs(form)
  Object.assign(fieldErrors, pairResult.fieldErrors)
  missingLabels.push(...pairResult.missingLabels)

  const hasFormatErrors = Object.keys(fieldErrors).some(
    (key) => fieldErrors[key] && fieldErrors[key] !== "Required"
  )
  const valid =
    missingLabels.length === 0 &&
    !hasFormatErrors &&
    Object.keys(fieldErrors).length === 0

  let message = ""
  if (!valid) {
    if (missingLabels.length) {
      message = `Looks like you missed some of the required fields:\n\n${missingLabels.join(
        "\n"
      )}`
    } else if (hasFormatErrors) {
      message =
        "Please fix the highlighted fields:\n\n" +
        [...new Set(Object.values(fieldErrors).filter(Boolean))].join("\n")
    }
  }

  return {
    valid,
    missingLabels,
    fieldErrors,
    message,
  }
}

export function buildFormFromApi(api) {
  const f = emptyForm()
  if (!api || typeof api !== "object") {
    return f
  }
  for (const [snake, camel] of Object.entries(SNAKE_TO_CAMEL)) {
    if (Object.prototype.hasOwnProperty.call(api, snake)) {
      f[camel] = String(api[snake] ?? "").trim()
    } else if (Object.prototype.hasOwnProperty.call(api, camel)) {
      f[camel] = String(api[camel] ?? "").trim()
    }
  }
  f.differentBrokerOfRecord = isDifferentBrokerOfRecord(api)
  if (!f.differentBrokerOfRecord && brokerOfRecordFieldsPopulated(f)) {
    f.differentBrokerOfRecord = true
  }
  f.additionalOffice = parseIntakeBoolean(
    api.additional_office ?? api.additionalOffice
  )
  f.secondAdditionalOffice = parseIntakeBoolean(
    api.second_additional_office ?? api.secondAdditionalOffice
  )
  f.thirdAdditionalOffice = parseIntakeBoolean(
    api.third_additional_office ?? api.thirdAdditionalOffice
  )
  f.fourPlusOffices = parseIntakeBoolean(
    api.four_plus_offices ?? api.fourPlusOffices
  )
  f.gender = mapGenderFromApi(api.gender ?? f.gender)
  f.brokerOfRecordGender = mapGenderFromApi(
    api.broker_of_record_gender ??
      api.brokerOfRecordGender ??
      f.brokerOfRecordGender
  )
  if (!f.differentBrokerOfRecord) {
    for (const key of BROKER_OF_RECORD_DETAIL_FIELD_KEYS) {
      f[key] = ""
    }
  }
  f.acceptPartTimers = mapYesNoFromApi(
    api.accept_part_timers ?? f.acceptPartTimers
  )
  f.acceptCommercialAgents = mapYesNoFromApi(
    api.accept_commercial_agents ?? f.acceptCommercialAgents
  )
  f.acceptPropertyManagers = mapYesNoFromApi(
    api.accept_property_managers ?? f.acceptPropertyManagers
  )
  f.acceptLoanOfficers = mapYesNoFromApi(
    api.accept_loan_officers ?? f.acceptLoanOfficers
  )
  f.acceptNewAgents = mapAcceptNewAgentsFromApi(
    api.accept_new_agents ?? f.acceptNewAgents
  )
  f.brokerageType = normalizeBrokerageTypeValue(
    api.brokerage_type ?? f.brokerageType
  )
  f.brand = normalizeBrandValue(api.brand ?? f.brand)
  f.state = normalizeStateValue(api.state ?? f.state)
  f.timezone = normalizeTimezoneValue(api.timezone ?? f.timezone)
  return applyAdditionalOfficePayloadRules(f)
}

export function yesNoOptionLabel(value) {
  const o = YES_NO_OPTIONS.find((x) => x.value === value)
  return o ? o.label : "—"
}

export function acceptNewAgentsLabel(value) {
  const o = ACCEPT_NEW_AGENTS_OPTIONS.find((x) => x.value === value)
  return o ? o.label : "—"
}

function displayOrDash(value) {
  const s = String(value ?? "").trim()
  return s || "—"
}

/**
 * Read-only display sections — same categories and order as the intake form.
 * @type {{ title: string, subheader?: string, specs: { key: string, label: string, format?: string, website?: boolean }[] }[]}
 */
export const INTAKE_DISPLAY_SECTIONS = [
  {
    title: "Website source",
    subheader: `${WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL} for this profile.`,
    specs: [
      {
        key: "websiteUrl",
        label: WEBSITE_ABOUT_OR_RECRUITING_PAGE_LABEL,
        website: true,
      },
    ],
  },
  {
    title: "Contact",
    subheader: "Primary contact details.",
    specs: [
      { key: "firstName", label: "First Name" },
      { key: "firstNamePhonetics", label: "First Name (phonetics)" },
      { key: "lastName", label: "Last Name" },
      { key: "lastNamePhonetics", label: "Last Name (phonetics)" },
      { key: "title", label: "Title" },
      { key: "email", label: "Email" },
      { key: "mobileNumber", label: "Mobile Phone Number" },
      { key: "gender", label: "Gender", format: "gender" },
    ],
  },
  {
    title: "Brokerage",
    subheader: "Brokerage identity and phone.",
    specs: [
      { key: "brokerageName", label: "Brokerage Name" },
      { key: "brokerageNamePhonetics", label: "Brokerage Name (phonetics)" },
      {
        key: "brokerageType",
        label: "Brokerage Type",
        format: "brokerageType",
      },
      {
        key: "differentBrokerOfRecord",
        label: "Different Broker of Record?",
        format: "boolean",
      },
      { key: "brokerOfRecord", label: "Broker of Record", borDetail: true },
      {
        key: "brokerOfRecordPhonetics",
        label: "Broker of Record Name Phonetics",
        borDetail: true,
      },
      {
        key: "brokerOfRecordPhone",
        label: "Broker of Record Phone Number",
        borDetail: true,
      },
      {
        key: "brokerOfRecordEmail",
        label: "Broker of Record Email",
        borDetail: true,
      },
      {
        key: "brokerOfRecordGender",
        label: "Broker of Record Gender",
        format: "gender",
        borDetail: true,
      },
      { key: "brokeragePhone", label: "Brokerage Phone" },
      { key: "brand", label: "Brand", format: "brand" },
    ],
  },
  {
    title: RECRUIT_PROFILE_SECTION_TITLES.officeAddress,
    subheader: "Primary office address and landmark.",
    specs: [
      { key: "streetAddress", label: "Street Address" },
      { key: "streetAddressPhonetics", label: "Street Address (phonetics)" },
      { key: "city", label: "Primary City" },
      { key: "cityPhonetics", label: "City Name Phonetics" },
      { key: "state", label: "State", format: "state" },
      { key: "zipcode", label: "Zip Code" },
      { key: "timezone", label: "Time Zone", format: "timezone" },
      { key: "localLandmark", label: "Intersection or local landmark" },
      {
        key: "localLandmarkPhonetics",
        label: "Intersection or local landmark (phonetics)",
      },
      { key: "numberOfOffices", label: "No. of Offices" },
    ],
  },
  {
    title: RECRUIT_PROFILE_SECTION_TITLES.additionalOffices,
    subheader: "Optional extra office locations beyond the primary address.",
    specs: [],
    isAdditionalOffices: true,
  },
  {
    title: RECRUIT_PROFILE_SECTION_TITLES.officesScale,
    subheader: "Scale and trust signals.",
    specs: [
      { key: "numberOfAgents", label: "No. of Agents" },
      { key: "yearsInBusiness", label: "Years in Business" },
      { key: "credibilityStatement", label: "Credibility Statement" },
    ],
  },
  {
    title: "Key features",
    subheader: "Up to three differentiators (full and short labels).",
    specs: [
      { key: "keyFeature1", label: "Key Feature 1" },
      { key: "keyFeature1Short", label: "Key Feature 1 Short" },
      { key: "keyFeature2", label: "Key Feature 2" },
      { key: "keyFeature2Short", label: "Key Feature 2 Short" },
      { key: "keyFeature3", label: "Key Feature 3" },
      { key: "keyFeature3Short", label: "Key Feature 3 Short" },
    ],
  },
  {
    title: "Bio highlights",
    subheader:
      "Optional personal details for Signature Campaigns — beyond typical real estate credentials.",
    specs: [
      {
        key: "professionalBioHighlights",
        label: "Professional Bio Highlights",
      },
      { key: "personalBioHighlights", label: "Personal Bio Highlights" },
    ],
  },
  {
    title: RECRUIT_PROFILE_SECTION_TITLES.commonObjections,
    subheader: "Common objections agents raise when considering a move.",
    specs: OBJECTION_RECOMMENDATION_PAIRS.flatMap((pair) => [
      { key: pair.objectionKey, label: pair.objectionLabel },
      { key: pair.recommendationKey, label: pair.recommendationLabel },
    ]),
  },
  {
    title: "Recruiting acceptance",
    subheader: "Who the brokerage accepts.",
    specs: [
      {
        key: "acceptPartTimers",
        label: "Accept Part Timers?",
        format: "yesNo",
      },
      {
        key: "acceptCommercialAgents",
        label: "Commercial Agents?",
        format: "yesNo",
      },
      {
        key: "acceptPropertyManagers",
        label: "Property Managers?",
        format: "yesNo",
      },
      {
        key: "acceptLoanOfficers",
        label: "Mortgage Brokers?",
        format: "yesNo",
      },
      {
        key: "acceptNewAgents",
        label: "Accept New Agents?",
        format: "acceptNewAgents",
      },
    ],
  },
]

function formatIntakeFieldDisplay(spec, intake, websiteUrl) {
  if (spec.website) {
    return displayOrDash(websiteUrl || intake.websiteUrl)
  }
  const raw = intake[spec.key]
  if (spec.format === "gender") {
    return genderOptionLabel(raw)
  }
  if (spec.format === "yesNo") {
    return yesNoOptionLabel(raw)
  }
  if (spec.format === "acceptNewAgents") {
    return acceptNewAgentsLabel(raw)
  }
  if (spec.format === "brokerageType") {
    return brokerageTypeOptionLabel(raw)
  }
  if (spec.format === "brand") {
    return brandOptionLabel(raw)
  }
  if (spec.format === "state") {
    return stateOptionLabel(raw)
  }
  if (spec.format === "timezone") {
    return timezoneOptionLabel(raw)
  }
  if (spec.format === "boolean") {
    return raw ? "Yes" : "No"
  }
  return displayOrDash(raw)
}

export function filterBrokerageDisplaySpecs(fieldSpecs, intake) {
  if (isDifferentBrokerOfRecord(intake)) {
    return fieldSpecs
  }
  return fieldSpecs.filter((spec) => !spec.borDetail)
}

/** @returns {[string, string][]} */
export function buildIntakeSectionRows(intake, websiteUrl, fieldSpecs) {
  return fieldSpecs.map((spec) => [
    spec.label,
    formatIntakeFieldDisplay(spec, intake, websiteUrl),
  ])
}

/** Convert GET/POST camelCase intake to snake_case for `buildFormFromApi`. */
export function intakeCamelToSnakeApi(intake) {
  if (!intake || typeof intake !== "object") {
    return null
  }
  const out = {}
  for (const [snake, camel] of Object.entries(SNAKE_TO_CAMEL)) {
    if (Object.prototype.hasOwnProperty.call(intake, camel)) {
      out[snake] = intake[camel]
    }
  }
  for (const [camel, snake] of Object.entries(INTAKE_BOOLEAN_CAMEL_TO_SNAKE)) {
    if (Object.prototype.hasOwnProperty.call(intake, camel)) {
      out[snake] = intake[camel]
    }
  }
  return out
}

/** Prefer non-empty website extraction values; keep saved intake for the rest. */
export function mergeWebsitePrefillWithSavedIntake(apiData, intakeCamel) {
  const savedSnake = intakeCamelToSnakeApi(intakeCamel)
  if (!savedSnake) {
    return apiData && typeof apiData === "object" ? apiData : null
  }
  if (!apiData || typeof apiData !== "object") {
    return savedSnake
  }
  const merged = { ...savedSnake }
  for (const [key, value] of Object.entries(apiData)) {
    if (value != null && String(value).trim() !== "") {
      merged[key] = value
    }
  }
  return merged
}

/** @returns {{ title: string, subheader?: string, rows: [string, string][], officeBlocks?: object[], locationGroups?: object[] }[]} */
export function buildPreviewSections(intake, websiteUrl) {
  return INTAKE_DISPLAY_SECTIONS.map((section) => {
    if (section.isAdditionalOffices) {
      return {
        title: section.title,
        subheader: section.subheader,
        rows: buildAdditionalOfficeDisplayRows(intake),
        officeBlocks: buildAdditionalOfficeDisplayBlocks(intake),
      }
    }
    const specs =
      section.title === "Brokerage"
        ? filterBrokerageDisplaySpecs(section.specs, intake)
        : section.specs
    const rows = buildIntakeSectionRows(intake, websiteUrl, specs)

    if (section.title === RECRUIT_PROFILE_SECTION_TITLES.officeAddress) {
      const addressKeys = new Set([
        "streetAddress",
        "streetAddressPhonetics",
        "city",
        "cityPhonetics",
        "state",
        "zipcode",
        "timezone",
      ])
      const landmarkKeys = new Set([
        "localLandmark",
        "localLandmarkPhonetics",
        "numberOfOffices",
      ])
      const addressRows = []
      const landmarkRows = []
      for (const spec of specs) {
        const row = [
          spec.label,
          formatIntakeFieldDisplay(spec, intake, websiteUrl),
        ]
        if (addressKeys.has(spec.key)) {
          addressRows.push(row)
        } else if (landmarkKeys.has(spec.key)) {
          landmarkRows.push(row)
        }
      }
      return {
        title: section.title,
        subheader: section.subheader,
        rows,
        locationGroups: [
          { title: "Street & city", rows: addressRows },
          { title: "Landmark & office count", rows: landmarkRows },
        ],
      }
    }

    return {
      title: section.title,
      subheader: section.subheader,
      rows,
    }
  })
}

/** @returns {[string, string][]} */
export function buildPreviewRows(form, websiteUrl) {
  return buildPreviewSections(form, websiteUrl).flatMap(
    (section) => section.rows
  )
}

/** POST body for CRM intake (camelCase keys). */
export function formToSubmitPayload(form, websiteUrl) {
  const differentBrokerOfRecord = isDifferentBrokerOfRecord(form)
  let payload = {
    ...form,
    websiteUrl,
    differentBrokerOfRecord,
    gender: normalizeGenderValue(form.gender),
    brand: normalizeBrandValue(form.brand),
    state: normalizeStateValue(form.state),
    timezone: normalizeTimezoneValue(form.timezone),
  }
  if (differentBrokerOfRecord) {
    payload.brokerOfRecordGender = normalizeGenderValue(
      form.brokerOfRecordGender
    )
  } else {
    for (const key of BROKER_OF_RECORD_DETAIL_FIELD_KEYS) {
      payload[key] = ""
    }
  }
  payload = applyAdditionalOfficePayloadRules(payload)
  return payload
}

/** POST body for CRM intake with optional Airtable sync control. */
export function formToApiPayload(
  form,
  websiteUrl,
  { syncToAirtable = true } = {}
) {
  return {
    ...formToSubmitPayload(form, websiteUrl),
    syncToAirtable,
  }
}
